import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import './DataPage.css';

const styles = {
  page: {
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    fontFamily: 'Inter, Roboto, Open Sans, sans-serif',
  },
  breadcrumb: {
    fontSize: '0.85rem',
    color: '#666',
    marginBottom: '0.5rem',
  },
  breadcrumbLink: {
    color: '#1976d2',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
  statsRow: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
  },
  statCard: {
    background: '#fff',
    borderRadius: '8px',
    padding: '1.25rem 1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    minWidth: '180px',
    borderLeft: '4px solid #1976d2',
  },
  statLabel: {
    fontSize: '0.75rem',
    color: '#666',
    textTransform: 'uppercase',
    fontWeight: '600',
    marginBottom: '0.25rem',
    letterSpacing: '0.05em',
  },
  statValue: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: '#333',
  },
  searchCard: {
    background: '#fff',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '1.5rem',
  },
  searchInput: {
    width: '100%',
    padding: '0.65rem 1rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    color: '#333',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  tableCard: {
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  tableResponsive: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '2px solid #e5e7eb',
    backgroundColor: '#fafafa',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.75rem 1rem',
    fontSize: '0.9rem',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
  },
  trEven: {
    backgroundColor: '#fafafa',
  },
  trOdd: {
    backgroundColor: '#fff',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    fontSize: '1rem',
    color: '#666',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#999',
    fontSize: '0.95rem',
  },
  orderBadge: {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '600',
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    minWidth: '28px',
    textAlign: 'center',
  },
};

function Bancadas() {
  const [bancadas, setBancadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hoveredRow, setHoveredRow] = useState(null);

  useEffect(() => {
    fetchBancadas();
  }, []);

  const fetchBancadas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/bancadas`);
      const data = response.data.data || response.data || [];
      setBancadas(data);
    } catch (err) {
      console.error('Erro ao buscar bancadas:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = bancadas.filter((b) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return b.DESCRICAO && b.DESCRICAO.toLowerCase().includes(term);
  });

  if (loading) {
    return (
      <div className="data-page" style={styles.page}>
        <div style={styles.loading}>Carregando bancadas...</div>
      </div>
    );
  }

  return (
    <div className="data-page" style={styles.page}>
      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink}>Inicio</span>
        <span> / </span>
        <span>Bancadas</span>
      </div>

      {/* Header */}
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>🔬</span>
          <div>
            <h1 style={styles.pageTitle}>Bancadas</h1>
            <p style={styles.pageSubtitle}>
              Gerenciamento de bancadas do laboratorio
            </p>
          </div>
        </div>
      </div>

      {/* Contadores */}
      <div className="responsive-flex" style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total de Bancadas</div>
          <div style={styles.statValue}>{filtered.length}</div>
        </div>
      </div>

      {/* Campo de Busca */}
      <div style={styles.searchCard}>
        <input
          type="text"
          placeholder="Buscar por descricao..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Tabela */}
      <div style={styles.tableCard}>
        <div style={styles.tableResponsive}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '70px' }}>ID</th>
                <th style={styles.th}>Descricao</th>
                <th style={styles.th}>Setor</th>
                <th style={styles.th}>Responsavel</th>
                <th style={{ ...styles.th, textAlign: 'center', width: '80px' }}>Ordem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="5" style={styles.emptyState}>
                    Nenhuma bancada encontrada.
                  </td>
                </tr>
              ) : (
                filtered.map((bancada, index) => (
                  <tr
                    key={bancada.ID || index}
                    onMouseEnter={() => setHoveredRow(index)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{
                      ...(index % 2 === 0 ? styles.trEven : styles.trOdd),
                      ...(hoveredRow === index ? { backgroundColor: '#e3f2fd' } : {}),
                    }}
                  >
                    <td style={styles.td}>
                      <strong>{bancada.ID || '-'}</strong>
                    </td>
                    <td style={styles.td}>{bancada.DESCRICAO || '-'}</td>
                    <td style={styles.td}>{bancada.SETOR_NOME || bancada.SETOR || '-'}</td>
                    <td style={styles.td}>{bancada.RESPONSAVEL_NOME || bancada.RESPONSAVEL || '-'}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {bancada.ORDEM != null ? (
                        <span style={styles.orderBadge}>{bancada.ORDEM}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Bancadas;
